import { useQuery, useQueries } from '@tanstack/react-query'
import { inventoriesApi } from '@/api/clients/inventoriesApi'
import { economicsApi } from '@/api/clients/economicsApi'
import type { Impact } from '@/api/types'

export type RateView = 'total' | 'daily' | 'hourly'

export interface ImpactTree {
  root: Impact | null
  isLoading: boolean
  error: unknown
  currency: string
}

// Builds the org → inventory → source → asset $-at-risk tree by fetching each
// inventory's hierarchical impact (§5.8 treemap/sunburst; rate = §12.5.4).
export function useImpactTree(orgId: string, rate: RateView): ImpactTree {
  const inventories = useQuery({
    queryKey: ['inventories', orgId],
    queryFn: () => inventoriesApi.list(orgId),
    enabled: !!orgId,
  })

  const invs = inventories.data ?? []
  const impacts = useQueries({
    queries: invs.map((inv) => ({
      queryKey: ['inv-impact', orgId, inv.id, rate],
      queryFn: () => economicsApi.inventoryImpact(orgId, inv.id, rate),
      enabled: !!orgId,
    })),
  })

  const isLoading = inventories.isLoading || impacts.some((q) => q.isLoading)
  const error = inventories.error ?? impacts.find((q) => q.error)?.error ?? null
  const children = impacts.map((q) => q.data).filter((d): d is Impact => !!d)
  const currency = children[0]?.currency ?? 'USD'
  const total = children.reduce((sum, c) => sum + (c.total_at_risk ?? 0), 0)

  const root: Impact | null =
    children.length > 0
      ? {
          level: 'org',
          id: 'org',
          name: 'Organisation',
          total_at_risk: total,
          currency,
          provenance: [],
          confidence: 'medium',
          components: {},
          rate_view: rate,
          children,
        }
      : null

  return { root, isLoading, error, currency }
}
